/**
 * Shared date helpers for Tent Finder.
 * Prefer local calendar dates (not UTC midnight) to avoid off-by-one day bugs.
 */

/**
 * Convert a Firestore Timestamp, Date, seconds object, or ISO string into a Date.
 * @returns {Date|null}
 */
export function toDate(value) {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (typeof value.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date for display (long US locale).
 */
export function formatDisplayDate(value, options = {}) {
  const d = toDate(value);
  if (!d) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  });
}

/**
 * Format a date+time for admin tables.
 */
export function formatDisplayDateTime(value) {
  const d = toDate(value);
  if (!d) return 'N/A';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * YYYY-MM-DD in local time (safe for <input type="date">).
 */
export function toLocalDateInputValue(value) {
  const d = toDate(value);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Start of today in local time.
 */
export function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * True if the event date is today or in the future (or has no date).
 */
export function isUpcomingEvent(event) {
  if (!event || !event.date) return true;
  const d = toDate(event.date);
  if (!d) return true;
  return d >= startOfToday();
}

/**
 * Sort events: soonest first; undated last.
 */
export function sortEventsAscending(events) {
  return [...events].sort((a, b) => {
    if (a.date && b.date) {
      const dateA = toDate(a.date);
      const dateB = toDate(b.date);
      if (dateA && dateB) return dateA - dateB;
    }
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });
}
